// Quattro game engine — pure functions, no React.
// 4 players, 6x6 grid, 4-in-a-row of same color wins.

export const PLAYERS = [
  { id: 0, key: "yellow_clubs", color: "#FFD700", glow: "rgba(255,215,0,0.55)", suit: "♣", name: "Clubs" },
  { id: 1, key: "blue_diamonds", color: "#00E5FF", glow: "rgba(0,229,255,0.55)", suit: "♦", name: "Diamonds" },
  { id: 2, key: "red_hearts", color: "#FF2A2A", glow: "rgba(255,42,42,0.55)", suit: "♥", name: "Hearts" },
  { id: 3, key: "purple_spades", color: "#B537F2", glow: "rgba(181,55,242,0.55)", suit: "♠", name: "Spades" },
];

export const SPECIALS = ["power_up", "power_down", "blocker", "color_switcher"];

export const SPECIAL_LABEL = {
  power_up: "Power Up",
  power_down: "Power Down",
  blocker: "Blocker",
  color_switcher: "Color Switcher",
};

export const SPECIAL_DESC = {
  power_up: "Boost one of YOUR top cards by +3 power.",
  power_down: "Reduce an opponent top card by 3 power.",
  blocker: "Lock an empty adjacent cell — no one can place there.",
  color_switcher: "Switch any opponent top card to your color.",
};

const BOARD_SIZE = 6;

function rng(seed) {
  // Mulberry32
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

export const MIN_CARD_POWER = 1;
export const MAX_CARD_POWER = 9;

/**
 * Single source of truth for card power bounds.
 *   - Clamps the value into [MIN_CARD_POWER, MAX_CARD_POWER].
 *   - In development, asserts when a caller tries to set something out of range.
 *   - Zero is reserved for special (non-numeric) cards and allowed if allowZero=true.
 */
export function clampPower(value, allowZero = false) {
  const v = Number.isFinite(value) ? Math.trunc(value) : NaN;
  if (Number.isNaN(v)) {
    throw new Error(`clampPower: expected a finite number, got ${value}`);
  }
  if (allowZero && v === 0) return 0;
  if (process.env.NODE_ENV !== "production") {
    if (v < MIN_CARD_POWER || v > MAX_CARD_POWER) {
      // Surface early in dev so a regression never slips to prod.
      console.warn(`clampPower: value ${v} out of range [${MIN_CARD_POWER},${MAX_CARD_POWER}] — clamping.`);
    }
  }
  if (v < MIN_CARD_POWER) return MIN_CARD_POWER;
  if (v > MAX_CARD_POWER) return MAX_CARD_POWER;
  return v;
}

export function shuffle(arr, rand = Math.random) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildDeck(playerId, rand, specialType) {
  // Per official Quattro rules: card power is strictly 1-9. Each player gets
  // 18 numeric cards = 2 copies of each power 1..9 + 1 special card.
  const dist = [1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7, 8, 8, 9, 9];
  const cards = shuffle(dist, rand).map((p, i) => ({
    id: `p${playerId}-c${i}`,
    owner: playerId,
    power: clampPower(p),
    special: null,
  }));
  cards.splice(Math.floor(rand() * cards.length), 0, {
    id: `p${playerId}-special`,
    owner: playerId,
    power: 0,
    special: specialType,
  });
  return cards;
}

export function createInitialState(seed = Date.now()) {
  const rand = rng(seed);
  // Each game contains all 4 unique specials, one per player (shuffled)
  const specialAssignments = shuffle(SPECIALS, rand);
  const decks = PLAYERS.map((p) => buildDeck(p.id, rand, specialAssignments[p.id]));
  const board = Array.from({ length: BOARD_SIZE }, () =>
    Array.from({ length: BOARD_SIZE }, () => ({ stack: [], blocked: false, blockedBy: null }))
  );
  return {
    board,
    decks,
    currentPlayer: 0,
    drawnCard: null,
    turnCount: 0,
    winner: null,
    winLine: null,
    log: [],
    seed,
  };
}

export function topCard(cell) {
  if (!cell || cell.blocked) return null;
  return cell.stack.length ? cell.stack[cell.stack.length - 1] : null;
}

export function inBounds(r, c) {
  return r >= 0 && r < BOARD_SIZE && c >= 0 && c < BOARD_SIZE;
}

export function hasAnyPlacement(board) {
  for (let r = 0; r < BOARD_SIZE; r++)
    for (let c = 0; c < BOARD_SIZE; c++)
      if (board[r][c].stack.length > 0 || board[r][c].blocked) return true;
  return false;
}

export function isAdjacentToAny(board, r, c) {
  for (let dr = -1; dr <= 1; dr++)
    for (let dc = -1; dc <= 1; dc++) {
      if (dr === 0 && dc === 0) continue;
      const nr = r + dr, nc = c + dc;
      if (inBounds(nr, nc) && (board[nr][nc].stack.length > 0 || board[nr][nc].blocked)) return true;
    }
  return false;
}

export function canPlaceNormal(state, r, c, card) {
  const cell = state.board[r][c];
  if (cell.blocked) return false;
  const top = topCard(cell);
  if (top) {
    return card.power > top.power;
  }
  // empty cell
  if (!hasAnyPlacement(state.board)) return true; // first move anywhere
  return isAdjacentToAny(state.board, r, c);
}

export function legalNormalMoves(state, card) {
  const moves = [];
  for (let r = 0; r < BOARD_SIZE; r++)
    for (let c = 0; c < BOARD_SIZE; c++)
      if (canPlaceNormal(state, r, c, card)) moves.push([r, c]);
  return moves;
}

// ---- Special move legality ----
export function legalSpecialTargets(state, card) {
  const owner = card.owner;
  const targets = [];
  for (let r = 0; r < BOARD_SIZE; r++) {
    for (let c = 0; c < BOARD_SIZE; c++) {
      const cell = state.board[r][c];
      const top = topCard(cell);
      if (card.special === "power_up") {
        if (top && top.owner === owner) targets.push([r, c]);
      } else if (card.special === "power_down") {
        if (top && top.owner !== owner) targets.push([r, c]);
      } else if (card.special === "blocker") {
        if (!cell.blocked && cell.stack.length === 0 &&
            (isAdjacentToAny(state.board, r, c) || !hasAnyPlacement(state.board))) targets.push([r, c]);
      } else if (card.special === "color_switcher") {
        if (top && top.owner !== owner) targets.push([r, c]);
      }
    }
  }
  return targets;
}

// ---- Mutations (return new state) ----
function clone(state) {
  return {
    ...state,
    board: state.board.map(row => row.map(cell => ({ ...cell, stack: cell.stack.map(c => ({ ...c })) }))),
    decks: state.decks.map(d => d.map(c => ({ ...c }))),
    log: [...state.log],
  };
}

export function drawCard(state) {
  const s = clone(state);
  if (s.winner !== null) return s;
  if (s.drawnCard) return s;
  const deck = s.decks[s.currentPlayer];
  if (!deck.length) {
    // skip player
    s.log.push(`P${s.currentPlayer + 1} has no cards, skipping.`);
    s.currentPlayer = (s.currentPlayer + 1) % 4;
    return drawCard(s);
  }
  s.drawnCard = deck.shift();
  return s;
}

/** Premium re-roll: discard the current drawn card to the bottom of the deck
 *  and draw a fresh one. Caller is responsible for charging gems first. */
export function rerollDrawnCard(state) {
  const s = clone(state);
  if (s.winner !== null || !s.drawnCard) return s;
  // Push the unwanted card to the BACK of the player's deck (not destroyed
  // — that keeps total card count constant and prevents abuse).
  s.decks[s.currentPlayer].push(s.drawnCard);
  s.drawnCard = null;
  s.log.push(`P${s.currentPlayer + 1} re-rolled their card.`);
  return drawCard(s);
}

function checkWinAt(board, r, c) {
  const top = topCard(board[r][c]);
  if (!top) return null;
  const dirs = [[0, 1], [1, 0], [1, 1], [1, -1]];
  for (const [dr, dc] of dirs) {
    let line = [[r, c]];
    // forward
    let nr = r + dr, nc = c + dc;
    while (inBounds(nr, nc)) {
      const t = topCard(board[nr][nc]);
      if (t && t.owner === top.owner) { line.push([nr, nc]); nr += dr; nc += dc; } else break;
    }
    // back
    nr = r - dr; nc = c - dc;
    while (inBounds(nr, nc)) {
      const t = topCard(board[nr][nc]);
      if (t && t.owner === top.owner) { line.unshift([nr, nc]); nr -= dr; nc -= dc; } else break;
    }
    if (line.length >= 4) return { owner: top.owner, line: line.slice(0, 4) };
  }
  return null;
}

export function checkWin(board) {
  for (let r = 0; r < BOARD_SIZE; r++)
    for (let c = 0; c < BOARD_SIZE; c++) {
      const w = checkWinAt(board, r, c);
      if (w) return w;
    }
  return null;
}

export function placeNormal(state, r, c) {
  if (!state.drawnCard) return state;
  if (!canPlaceNormal(state, r, c, state.drawnCard)) return state;
  const s = clone(state);
  const card = s.drawnCard;
  s.board[r][c].stack.push(card);
  s.drawnCard = null;
  s.turnCount += 1;
  s.log.push(`P${card.owner + 1} placed power ${card.power} at (${r},${c})`);
  const win = checkWin(s.board);
  if (win) {
    s.winner = win.owner;
    s.winLine = win.line;
    return s;
  }
  s.currentPlayer = (s.currentPlayer + 1) % 4;
  return s;
}

export function applySpecial(state, r, c) {
  if (!state.drawnCard || !state.drawnCard.special) return state;
  const card = state.drawnCard;
  const targets = legalSpecialTargets(state, card);
  if (!targets.some(([tr, tc]) => tr === r && tc === c)) return state;
  const s = clone(state);
  const cell = s.board[r][c];
  if (card.special === "power_up") {
    const top = cell.stack[cell.stack.length - 1];
    top.power = clampPower(top.power + 3);
    s.log.push(`P${card.owner + 1} powered up (${r},${c}) to ${top.power}`);
  } else if (card.special === "power_down") {
    const top = cell.stack[cell.stack.length - 1];
    top.power = clampPower(top.power - 3);
    s.log.push(`P${card.owner + 1} powered down (${r},${c}) to ${top.power}`);
  } else if (card.special === "blocker") {
    cell.blocked = true;
    cell.blockedBy = card.owner;
    s.log.push(`P${card.owner + 1} blocked (${r},${c})`);
  } else if (card.special === "color_switcher") {
    const top = cell.stack[cell.stack.length - 1];
    top.owner = card.owner;
    s.log.push(`P${card.owner + 1} switched (${r},${c}) to their color`);
  }
  s.drawnCard = null;
  s.turnCount += 1;
  const win = checkWin(s.board);
  if (win) {
    s.winner = win.owner;
    s.winLine = win.line;
    return s;
  }
  s.currentPlayer = (s.currentPlayer + 1) % 4;
  return s;
}

export function skipTurn(state) {
  // when no legal move exists for current player
  const s = clone(state);
  s.log.push(`P${s.currentPlayer + 1} has no legal move, skipped.`);
  s.drawnCard = null;
  s.currentPlayer = (s.currentPlayer + 1) % 4;
  return s;
}

export function isGameOver(state) {
  if (state.winner !== null) return true;
  if (state.decks.every(d => d.length === 0) && !state.drawnCard) return true;
  return false;
}

export const BOARD_DIM = BOARD_SIZE;
